/**
 * Демо-компания и демо-пользователь (ТЗ 6.30: ГЛ-02, ГЛ-03).
 *
 * Демо-компания — отдельная компания с флагом isDemo и вымышленными данными во всех разделах; каждую ночь
 * данные возвращаются к исходным. Демо-пользователь входит одной кнопкой без пароля: сессия на 2 часа,
 * роль выбирается при входе и переключается в шапке. Внешние каналы (почта, Telegram, 1С) у демо выключены.
 */
import { z } from 'zod';

/** Почтовый домен демо-пользователей: по нему портал узнаёт демо-сессию */
export const DEMO_EMAIL_DOMAIN = 'demo.epc-pilot.local';

/** Длительность демо-сессии */
export const DEMO_SESSION_HOURS = 2;

export const DEMO_ROLE_CODES = ['pm', 'buyer', 'estimator', 'foreman', 'contractor'] as const;
export type DemoRole = (typeof DEMO_ROLE_CODES)[number];

export interface DemoRoleInfo {
  code: DemoRole;
  title: string;
  /** Чем занимается и что видит — показывается при выборе роли */
  description: string;
  /** Вымышленное имя сотрудника демо-компании */
  fullName: string;
  /** Внешний участник (подрядчик) — не сотрудник компании */
  external?: boolean;
}

export const DEMO_ROLES: DemoRoleInfo[] = [
  {
    code: 'pm',
    title: 'Руководитель проекта',
    description:
      'Все разделы и все деньги: портфель, бюджет и маржа, закупки, договоры, график, ПСД и надзор, склад.',
    fullName: 'Айгерим Сапарова',
  },
  {
    code: 'buyer',
    title: 'Снабженец',
    description: 'Заявки на закуп, КП поставщиков, сравнение со сметой, договоры поставки, подбор со склада.',
    fullName: 'Ерлан Досжанов',
  },
  {
    code: 'estimator',
    title: 'Сметчик',
    description:
      'Сметы и нормативная база, себестоимость раздела и варианты исполнения, сравнение КП со сметой.',
    fullName: 'Дана Ибраева',
  },
  {
    code: 'foreman',
    title: 'Прораб',
    description:
      'Площадка: график работ, ПСД и замечания надзора, выдача со склада, поручения — без цен и сумм.',
    fullName: 'Марат Нурланов',
  },
  {
    code: 'contractor',
    title: 'Подрядчик',
    description:
      'Внешний участник: свои предписания и ПСД по своему договору, отчёт о давальческих материалах.',
    fullName: 'Виктор Ким',
    external: true,
  },
];

export function demoEmail(role: DemoRole): string {
  return `demo-${role}@${DEMO_EMAIL_DOMAIN}`;
}

/** Код демо-роли по почте пользователя; null — обычный пользователь */
export function demoRoleOf(email: string | null | undefined): DemoRole | null {
  if (!email) return null;
  const m = /^demo-([a-z]+)@(.+)$/.exec(email.toLowerCase());
  if (!m || m[2] !== DEMO_EMAIL_DOMAIN) return null;
  return (DEMO_ROLE_CODES as readonly string[]).includes(m[1]!) ? (m[1] as DemoRole) : null;
}

export const DemoLoginInput = z.object({ role: z.enum(DEMO_ROLE_CODES).default('pm') });
export type DemoLoginInput = z.infer<typeof DemoLoginInput>;

/** Что отдаёт открытая страница: можно ли попробовать демо и какие роли на выбор */
export const DemoInfo = z.object({
  enabled: z.boolean(),
  /** Название демо-компании — для кнопки и диалога на странице входа */
  companyName: z.string(),
  roles: z.array(
    z.object({
      code: z.enum(DEMO_ROLE_CODES),
      title: z.string(),
      description: z.string(),
      fullName: z.string(),
    }),
  ),
  /** Когда демо-данные возвращаются к исходным (по часам компании) */
  resetAt: z.string(),
});
export type DemoInfo = z.infer<typeof DemoInfo>;
